import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { formatEasternDateTime } from '../Utils/dateTime';
import {
  getErrorMessage,
  postSessionCompleteInspection,
  postSessionCompleteJob,
  postSessionDoneForDay,
  postSessionFlowSchedule,
  postSessionOnTheWay,
  postSessionPause,
  postSessionResume,
  postSessionStart,
} from '../Config/adminSessionFlowService';

function inputClass(extra = '') {
  return `w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${extra}`;
}

function labelClass() {
  return 'block text-sm font-medium text-gray-700 mb-1';
}

function bigChoiceBtn(extra = '') {
  return `w-full rounded-xl border-2 border-indigo-200 bg-white px-4 py-4 text-left text-base font-medium text-gray-900 shadow-sm transition hover:border-indigo-400 hover:bg-indigo-50/60 disabled:opacity-50 ${extra}`;
}

export function normalizeSessionId(session) {
  if (!session || typeof session !== 'object') return '';
  return (
    session.session_id ?? session.inspection_session_id ?? session.id ?? session.sessionId ?? ''
  );
}

function normalizeStoredStatus(session) {
  return String(session?.status ?? '').toUpperCase();
}

function effectiveStatusUpper(session) {
  return String(
    session?.effective_status ?? session?.effectiveStatus ?? session?.status ?? ''
  ).toUpperCase();
}

function sessionTypeUpper(session) {
  return String(session?.session_type ?? session?.type ?? '').toUpperCase();
}

function sessionEnded(session) {
  return Boolean(session?.ended_at ?? session?.endedAt);
}

function sessionOnTheWayAt(session) {
  if (!session || typeof session !== 'object') return null;
  const raw =
    session.on_the_way_at ?? session.onTheWayAt ?? session.on_the_way ?? session.onTheWay ?? null;
  if (raw == null || typeof raw === 'boolean') return null;
  return raw;
}

function sessionIsOnTheWay(session) {
  if (!session || typeof session !== 'object') return false;
  if (session.on_the_way === true || session.onTheWay === true) return true;
  return Boolean(sessionOnTheWayAt(session));
}

function sessionScheduledVisitAt(session) {
  if (!session || typeof session !== 'object') return null;
  return (
    session.scheduled_visit_at ??
    session.scheduledVisitAt ??
    session.scheduled_at ??
    session.scheduledAt ??
    null
  );
}

function pickPrimaryOpenSession(sessions) {
  if (!Array.isArray(sessions) || sessions.length === 0) return null;
  const open = sessions.filter((s) => !sessionEnded(s));
  if (open.length === 0) return null;
  const paused = open.filter((s) => effectiveStatusUpper(s) === 'PAUSED');
  if (paused.length > 0) {
    paused.sort(
      (a, b) =>
        (Date.parse(b.updated_at ?? b.created_at ?? 0) || 0) -
        (Date.parse(a.updated_at ?? a.created_at ?? 0) || 0)
    );
    return paused[0];
  }
  open.sort(
    (a, b) =>
      (Date.parse(b.updated_at ?? b.created_at ?? 0) || 0) -
      (Date.parse(a.updated_at ?? a.created_at ?? 0) || 0)
  );
  return open[0];
}

/** Drive buttons from sessions + order status (no separate ui-state call). */
export function deriveSuggestedStep(sessions, orderStatus) {
  const ou = String(orderStatus || '').toUpperCase();
  if (['DONE', 'COMPLETE', 'CANCELED', 'CANCELLED'].includes(ou)) return 'terminal';

  const open = Array.isArray(sessions) ? sessions.filter((s) => !sessionEnded(s)) : [];
  if (open.length === 0) {
    return Array.isArray(sessions) && sessions.length > 0 ? 'schedule_next_session' : 'schedule_first_session';
  }

  const s = pickPrimaryOpenSession(sessions);
  if (!s) return 'schedule_first_session';

  if (effectiveStatusUpper(s) === 'PAUSED') return 'resume';

  const st = sessionTypeUpper(s);
  const sched = sessionScheduledVisitAt(s);
  const otw = sessionIsOnTheWay(s);
  const started = Boolean(s.started_at ?? s.startedAt);

  if (!sched && st) return 'schedule_first_session';
  if (!otw) return 'mark_on_the_way';
  if (!started) return 'start_session';
  if (st === 'INSPECTION') return 'inspection_in_progress';
  if (st === 'JOB') return 'job_in_progress';
  return 'start_session';
}

function isoFromDatetimeLocal(localStr) {
  if (!localStr || typeof localStr !== 'string') return null;
  const d = new Date(localStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function hasPendingQuotationBlock(serviceType, quotation) {
  const st = String(serviceType || '').toUpperCase();
  if (!['QUOTATION', 'DIAGNOSTIC', 'AUTO'].includes(st)) return false;
  const rows = Array.isArray(quotation) ? quotation : quotation ? [quotation] : [];
  return rows.some((q) => String(q?.status || '').toUpperCase() === 'PENDING');
}

/**
 * Order visits: plain-language steps, `GET/POST …/admin/order-flow/orders/:orderId/sessions/…`.
 */
export default function AdminSessionFlowPanel({
  orderId,
  orderStatus,
  orderWorker,
  orderContractorId,
  disabled,
  sessions: sessionsProp,
  flowLoading,
  onRefreshAll,
  orderTimeOptions = [],
  serviceType,
  orderQuotation,
}) {
  const [actionLoading, setActionLoading] = useState(null);
  /** `pick_kind` → two sentences only; `pick_time` → date/time + one confirm */
  const [schedulePhase, setSchedulePhase] = useState('pick_kind');
  const [scheduleKind, setScheduleKind] = useState('INSPECTION');
  const [scheduleAtLocal, setScheduleAtLocal] = useState('');
  const [otwEta, setOtwEta] = useState('');
  const [inspectionStep, setInspectionStep] = useState('is_done'); // is_done | after_done | another_date
  const [jobStep, setJobStep] = useState('all_done'); // all_done | how_continue
  const [scheduleJobAtLocal, setScheduleJobAtLocal] = useState('');
  const [scheduleNextInspAtLocal, setScheduleNextInspAtLocal] = useState('');
  const [pauseResumeAtLocal, setPauseResumeAtLocal] = useState('');
  const [doneForDayAtLocal, setDoneForDayAtLocal] = useState('');

  useEffect(() => {
    setSchedulePhase('pick_kind');
    setScheduleAtLocal('');
    setOtwEta('');
    setInspectionStep('is_done');
    setJobStep('all_done');
    setScheduleJobAtLocal('');
    setScheduleNextInspAtLocal('');
    setPauseResumeAtLocal('');
    setDoneForDayAtLocal('');
  }, [orderId]);

  const sessions = useMemo(() => (Array.isArray(sessionsProp) ? sessionsProp : []), [sessionsProp]);
  const suggestedStep = deriveSuggestedStep(sessions, orderStatus);
  const openSession = useMemo(() => pickPrimaryOpenSession(sessions), [sessions]);
  const openKey = useMemo(() => (openSession ? normalizeSessionId(openSession) : ''), [openSession]);
  const quotationBlocksJobStart =
    sessionTypeUpper(openSession) === 'JOB' && hasPendingQuotationBlock(serviceType, orderQuotation);

  useEffect(() => {
    if (suggestedStep === 'schedule_first_session' || suggestedStep === 'schedule_next_session') {
      setSchedulePhase('pick_kind');
    }
  }, [suggestedStep, sessions.length]);

  useEffect(() => {
    if (suggestedStep === 'inspection_in_progress') {
      setInspectionStep('is_done');
    }
    if (suggestedStep === 'job_in_progress') {
      setJobStep('all_done');
    }
  }, [suggestedStep, openKey]);

  const preferredIsoChips = useMemo(() => {
    if (!Array.isArray(orderTimeOptions)) return [];
    return [...new Set(orderTimeOptions.map((x) => String(x).trim()).filter(Boolean))];
  }, [orderTimeOptions]);

  const toastRun = async (key, fn) => {
    setActionLoading(key);
    try {
      const { ok, body } = await fn();
      if (ok && body?.success !== false) {
        toast.success('All set, we saved that.');
        if (typeof onRefreshAll === 'function') await onRefreshAll();
        return true;
      }
      toast.error(getErrorMessage(body, 'Something went wrong. Please try again.'));
      return false;
    } catch {
      toast.error('Something went wrong. Please try again.');
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const renderSessionsTable = () => (
    <div className="overflow-x-auto rounded-lg border border-violet-100 bg-white">
      <table className="min-w-full divide-y divide-gray-200 text-left text-xs">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-2 py-2 font-semibold text-gray-700">Visit type</th>
            <th className="px-2 py-2 font-semibold text-gray-700">Progress</th>
            <th className="px-2 py-2 font-semibold text-gray-700">Planned time</th>
            <th className="px-2 py-2 font-semibold text-gray-700">On the way</th>
            <th className="px-2 py-2 font-semibold text-gray-700">Started on site</th>
            <th className="px-2 py-2 font-semibold text-gray-700">Finished</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {sessions.length === 0 ? (
            <tr>
              <td colSpan={6} className="px-3 py-4 text-gray-500">
                Nothing booked yet. Use the questions below when you are ready to plan the first visit.
              </td>
            </tr>
          ) : (
            sessions.map((row, idx) => {
              const sid = normalizeSessionId(row);
              const st = sessionTypeUpper(row);
              const stat = effectiveStatusUpper(row) || normalizeStoredStatus(row);
              const sch = sessionScheduledVisitAt(row);
              const otw = sessionIsOnTheWay(row);
              const started = row.started_at ?? row.startedAt;
              const ended = row.ended_at ?? row.endedAt;
              const kindLabel = st === 'JOB' ? 'Paid job' : st === 'INSPECTION' ? 'Inspection' : st || '—';
              return (
                <tr key={sid || `row-${idx}`}>
                  <td className="px-2 py-2">{kindLabel}</td>
                  <td className="px-2 py-2">{stat}</td>
                  <td className="px-2 py-2">{sch ? formatEasternDateTime(sch) : '—'}</td>
                  <td className="px-2 py-2">{otw ? 'Yes' : '—'}</td>
                  <td className="px-2 py-2">{started ? formatEasternDateTime(started) : '—'}</td>
                  <td className="px-2 py-2">{ended ? formatEasternDateTime(ended) : '—'}</td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );

  if (flowLoading && sessions.length === 0) {
    return (
      <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-4">
        <p className="text-sm text-gray-600">One moment, loading visits for this order…</p>
      </div>
    );
  }

  const openId = openSession ? normalizeSessionId(openSession) : '';

  return (
    <div className="space-y-4 rounded-xl border border-violet-200 bg-violet-50/40 p-4 text-left">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">Visits for this order</h3>
        <p className="mt-1 text-xs text-gray-600">
          Take it one step at a time. Only tap the button that matches what is happening right now. Nothing here is
          technical — if you are unsure, ask a lead which visit you are on.
        </p>
      </div>

      {renderSessionsTable()}

      {suggestedStep === 'terminal' ? (
        <p className="text-sm text-gray-600">This order is finished or canceled. No visit steps are needed here.</p>
      ) : null}

      {(suggestedStep === 'schedule_first_session' || suggestedStep === 'schedule_next_session') && (
        <div className="space-y-4 rounded-lg border border-indigo-200 bg-white p-4">
          <p className="text-base font-semibold text-gray-900">
            {suggestedStep === 'schedule_first_session'
              ? 'Step 1 — What is the team doing on this first visit?'
              : 'Step 1 — What should we book next?'}
          </p>
          {!orderContractorId || !orderWorker ? (
            <p className="text-sm text-amber-900">
              Please assign a contractor and a worker on this order first. Then you can plan a visit here.
            </p>
          ) : schedulePhase === 'pick_kind' ? (
            <div className="flex flex-col gap-3 sm:max-w-lg">
              <button
                type="button"
                disabled={disabled}
                className={bigChoiceBtn()}
                onClick={() => {
                  setScheduleKind('INSPECTION');
                  setSchedulePhase('pick_time');
                }}
              >
                Are you going for an inspection visit first?
                <span className="mt-1 block text-sm font-normal text-gray-600">
                  Choose this when someone is only looking at the site, measuring, or preparing a quote — not doing the
                  paid work yet.
                </span>
              </button>
              <button
                type="button"
                disabled={disabled}
                className={bigChoiceBtn()}
                onClick={() => {
                  setScheduleKind('JOB');
                  setSchedulePhase('pick_time');
                }}
              >
                Are you going for the paid job visit?
                <span className="mt-1 block text-sm font-normal text-gray-600">
                  Choose this when the team is coming to do the actual work the customer booked.
                </span>
              </button>
            </div>
          ) : (
            <div className="space-y-4 sm:max-w-md">
              <p className="text-sm text-gray-700">
                Step 2 — When should this visit happen? Pick a date and time. You can change it later if plans shift.
              </p>
              <button
                type="button"
                className="mb-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                onClick={() => setSchedulePhase('pick_kind')}
                disabled={disabled}
              >
                Go back and change visit type
              </button>
              {preferredIsoChips.length > 0 ? (
                <div>
                  <p className="mb-1 text-xs font-medium text-gray-700">Times the customer already suggested</p>
                  <div className="flex flex-wrap gap-2">
                    {preferredIsoChips.map((iso) => (
                      <button
                        key={iso}
                        type="button"
                        disabled={disabled}
                        onClick={() => {
                          const d = new Date(iso);
                          if (!Number.isNaN(d.getTime())) {
                            const pad = (n) => String(n).padStart(2, '0');
                            const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
                            setScheduleAtLocal(local);
                          }
                        }}
                        className="rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-[11px] text-indigo-900 hover:bg-indigo-100"
                      >
                        {formatEasternDateTime(iso)}
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
              <div>
                <label className={labelClass()}>Date and time for this visit</label>
                <input
                  type="datetime-local"
                  className={inputClass()}
                  value={scheduleAtLocal}
                  onChange={(e) => setScheduleAtLocal(e.target.value)}
                  disabled={disabled}
                />
              </div>
              <button
                type="button"
                disabled={disabled || actionLoading === 'sched' || !scheduleAtLocal}
                onClick={() => {
                  const iso = isoFromDatetimeLocal(scheduleAtLocal);
                  if (!iso) {
                    toast.error('Please pick a real date and time.');
                    return;
                  }
                  const payload =
                    scheduleKind === 'INSPECTION'
                      ? {
                          session_type: scheduleKind,
                          scheduled_visit_at: iso,
                          visit_purpose: 'QUOTE_PREP',
                        }
                      : {
                          session_type: scheduleKind,
                          scheduled_visit_at: iso,
                        };
                  return toastRun('sched', () => postSessionFlowSchedule(orderId, payload));
                }}
                className="rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actionLoading === 'sched' ? 'Saving…' : 'Book this visit'}
              </button>
            </div>
          )}
        </div>
      )}

      {suggestedStep === 'mark_on_the_way' && !openId ? (
        <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950">
          We could not find an open visit to update. Refresh the page, or ask support if the list above looks wrong.
        </p>
      ) : null}

      {suggestedStep === 'mark_on_the_way' && openId ? (
        <div className="space-y-3 rounded-lg border border-sky-200 bg-white p-4 sm:max-w-md">
          <p className="text-base font-semibold text-gray-900">Is someone driving to the customer right now?</p>
          <p className="text-sm text-gray-600">
            Tap below only after the team has actually left for this visit. Optional: roughly how many minutes until
            arrival.
          </p>
          <div className="max-w-xs">
            <label className={labelClass()}>Minutes until arrival (optional)</label>
            <input
              type="number"
              min="0"
              className={inputClass()}
              value={otwEta}
              onChange={(e) => setOtwEta(e.target.value)}
              disabled={disabled}
              placeholder="e.g. 25"
            />
          </div>
          <button
            type="button"
            disabled={disabled || actionLoading === 'otw'}
            onClick={() => {
              const body = {};
              const n = Number(otwEta);
              if (otwEta !== '' && !Number.isNaN(n) && n >= 0) body.eta_minutes = Math.round(n);
              return toastRun('otw', () => postSessionOnTheWay(orderId, openId, body));
            }}
            className="w-full rounded-xl bg-sky-600 px-4 py-3 text-base font-semibold text-white hover:bg-sky-700 disabled:opacity-50 sm:w-auto"
          >
            {actionLoading === 'otw' ? 'Saving…' : 'Yes, we are on the way now'}
          </button>
        </div>
      ) : null}

      {suggestedStep === 'start_session' && openId ? (
        <div className="space-y-3 rounded-lg border border-emerald-200 bg-white p-4 sm:max-w-md">
          <p className="text-base font-semibold text-gray-900">Has the team arrived and started this visit?</p>
          <p className="text-sm text-gray-600">Use this when work or the walkthrough has actually begun at the address.</p>
          <button
            type="button"
            disabled={disabled || actionLoading === 'start' || quotationBlocksJobStart}
            onClick={() => toastRun('start', () => postSessionStart(orderId, openId, {}))}
            className="w-full rounded-xl bg-emerald-700 px-4 py-3 text-base font-semibold text-white hover:bg-emerald-800 disabled:opacity-50 sm:w-auto"
          >
            {actionLoading === 'start' ? 'Saving…' : 'Yes, we have started on site'}
          </button>
          {quotationBlocksJobStart ? (
            <p className="text-sm text-amber-900">
              The quotation still needs a decision before paid work can start. Finish that in the financial section
              first.
            </p>
          ) : null}
        </div>
      ) : null}

      {suggestedStep === 'resume' && openId ? (
        <div className="space-y-3 rounded-lg border border-amber-200 bg-white p-4 sm:max-w-md">
          <p className="text-base font-semibold text-gray-900">Ready to continue after the break?</p>
          <p className="text-sm text-gray-600">This is only for a pause on the same visit, still at the customer.</p>
          <button
            type="button"
            disabled={disabled || actionLoading === 'resume'}
            onClick={() => toastRun('resume', () => postSessionResume(orderId, openId))}
            className="w-full rounded-xl bg-amber-600 px-4 py-3 text-base font-semibold text-white hover:bg-amber-700 disabled:opacity-50 sm:w-auto"
          >
            {actionLoading === 'resume' ? 'Saving…' : 'Yes, continue the visit now'}
          </button>
        </div>
      ) : null}

      {suggestedStep === 'inspection_in_progress' && openId ? (
        <div className="space-y-4 rounded-lg border border-violet-200 bg-white p-4 sm:max-w-lg">
          {inspectionStep === 'is_done' ? (
            <>
              <p className="text-base font-semibold text-gray-900">How did this inspection visit go?</p>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  disabled={disabled}
                  className={bigChoiceBtn()}
                  onClick={() => setInspectionStep('after_done')}
                >
                  The inspection is finished for now.
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  className={bigChoiceBtn()}
                  onClick={() => setInspectionStep('another_date')}
                >
                  We need to come back for another inspection visit.
                </button>
              </div>
            </>
          ) : inspectionStep === 'after_done' ? (
            <>
              <p className="text-base font-semibold text-gray-900">What happens next with the paid job?</p>
              <button
                type="button"
                className="mb-3 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                onClick={() => setInspectionStep('is_done')}
                disabled={disabled}
              >
                Go back
              </button>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  disabled={disabled || actionLoading === 'ci-now'}
                  className={bigChoiceBtn()}
                  onClick={() => toastRun('ci-now', () => postSessionCompleteInspection(orderId, openId, { outcome: 'start_job_now' }))}
                >
                  Start the paid work right now at this same address.
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  className={bigChoiceBtn()}
                  onClick={() => setInspectionStep('schedule_job_later')}
                >
                  Schedule the paid job for another day.
                </button>
              </div>
            </>
          ) : inspectionStep === 'schedule_job_later' ? (
            <>
              <p className="text-sm font-semibold text-gray-900">When should the paid job happen?</p>
              <button
                type="button"
                className="mb-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                onClick={() => setInspectionStep('after_done')}
                disabled={disabled}
              >
                Go back
              </button>
              <input
                type="datetime-local"
                className={inputClass()}
                value={scheduleJobAtLocal}
                onChange={(e) => setScheduleJobAtLocal(e.target.value)}
                disabled={disabled}
              />
              <button
                type="button"
                disabled={disabled || actionLoading === 'ci-job' || !scheduleJobAtLocal}
                onClick={() => {
                  const iso = isoFromDatetimeLocal(scheduleJobAtLocal);
                  if (!iso) {
                    toast.error('Please pick a real date and time.');
                    return;
                  }
                  return toastRun('ci-job', () =>
                    postSessionCompleteInspection(orderId, openId, {
                      outcome: 'schedule_job',
                      scheduled_visit_at: iso,
                    })
                  );
                }}
                className="rounded-lg bg-indigo-600 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {actionLoading === 'ci-job' ? 'Saving…' : 'Save and close this inspection'}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm font-semibold text-gray-900">When is the next inspection visit?</p>
              <button
                type="button"
                className="mb-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                onClick={() => setInspectionStep('is_done')}
                disabled={disabled}
              >
                Go back
              </button>
              <input
                type="datetime-local"
                className={inputClass()}
                value={scheduleNextInspAtLocal}
                onChange={(e) => setScheduleNextInspAtLocal(e.target.value)}
                disabled={disabled}
              />
              <button
                type="button"
                disabled={disabled || actionLoading === 'ci-next' || !scheduleNextInspAtLocal}
                onClick={() => {
                  const iso = isoFromDatetimeLocal(scheduleNextInspAtLocal);
                  if (!iso) {
                    toast.error('Please pick a real date and time.');
                    return;
                  }
                  return toastRun('ci-next', () =>
                    postSessionCompleteInspection(orderId, openId, {
                      outcome: 'another_inspection',
                      scheduled_visit_at: iso,
                    })
                  );
                }}
                className="rounded-lg bg-violet-700 px-5 py-3 text-sm font-semibold text-white hover:bg-violet-800 disabled:opacity-50"
              >
                {actionLoading === 'ci-next' ? 'Saving…' : 'Save and book the next inspection'}
              </button>
            </>
          )}
        </div>
      ) : null}

      {suggestedStep === 'job_in_progress' && openId ? (
        <div className="space-y-4 rounded-lg border border-emerald-200 bg-white p-4 sm:max-w-lg">
          {jobStep === 'all_done' ? (
            <>
              <p className="text-base font-semibold text-gray-900">Is all work on this job finished for good?</p>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  disabled={disabled || actionLoading === 'cj'}
                  className={bigChoiceBtn()}
                  onClick={() => toastRun('cj', () => postSessionCompleteJob(orderId, openId, {}))}
                >
                  Yes, everything is done and we are closing this job.
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  className={bigChoiceBtn()}
                  onClick={() => setJobStep('how_continue')}
                >
                  No, there is still more work to do later.
                </button>
              </div>
            </>
          ) : jobStep === 'how_continue' ? (
            <>
              <p className="text-base font-semibold text-gray-900">What kind of pause is this?</p>
              <button
                type="button"
                className="mb-3 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                onClick={() => setJobStep('all_done')}
                disabled={disabled}
              >
                Go back
              </button>
              <div className="flex flex-col gap-3">
                <button
                  type="button"
                  disabled={disabled}
                  className={bigChoiceBtn()}
                  onClick={() => setJobStep('pause_same_site')}
                >
                  We are only taking a short break, still at the customer today.
                </button>
                <button
                  type="button"
                  disabled={disabled}
                  className={bigChoiceBtn()}
                  onClick={() => setJobStep('done_day')}
                >
                  We are done for today and will come back another day.
                </button>
              </div>
            </>
          ) : jobStep === 'pause_same_site' ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-900">When should work start again after this break?</p>
              <button
                type="button"
                className="mb-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                onClick={() => setJobStep('how_continue')}
                disabled={disabled}
              >
                Go back
              </button>
              <input
                type="datetime-local"
                className={inputClass()}
                value={pauseResumeAtLocal}
                onChange={(e) => setPauseResumeAtLocal(e.target.value)}
                disabled={disabled}
              />
              <button
                type="button"
                disabled={disabled || actionLoading === 'pause' || !pauseResumeAtLocal}
                onClick={() => {
                  const iso = isoFromDatetimeLocal(pauseResumeAtLocal);
                  if (!iso) {
                    toast.error('Please pick a time to continue.');
                    return;
                  }
                  return toastRun('pause', () =>
                    postSessionPause(orderId, openId, { scheduled_resume_at: iso })
                  );
                }}
                className="rounded-lg bg-sky-700 px-5 py-3 text-sm font-semibold text-white hover:bg-sky-800 disabled:opacity-50"
              >
                {actionLoading === 'pause' ? 'Saving…' : 'Save this break'}
              </button>
            </div>
          ) : jobStep === 'done_day' ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-900">When should the team come back?</p>
              <button
                type="button"
                className="mb-2 text-sm font-medium text-indigo-700 underline hover:text-indigo-900"
                onClick={() => setJobStep('how_continue')}
                disabled={disabled}
              >
                Go back
              </button>
              <input
                type="datetime-local"
                className={inputClass()}
                value={doneForDayAtLocal}
                onChange={(e) => setDoneForDayAtLocal(e.target.value)}
                disabled={disabled}
              />
              <button
                type="button"
                disabled={disabled || actionLoading === 'dfd' || !doneForDayAtLocal}
                onClick={() => {
                  const iso = isoFromDatetimeLocal(doneForDayAtLocal);
                  if (!iso) {
                    toast.error('Please pick the next visit time.');
                    return;
                  }
                  return toastRun('dfd', () =>
                    postSessionDoneForDay(orderId, openId, { scheduled_visit_at: iso })
                  );
                }}
                className="rounded-lg bg-indigo-700 px-5 py-3 text-sm font-semibold text-white hover:bg-indigo-800 disabled:opacity-50"
              >
                {actionLoading === 'dfd' ? 'Saving…' : 'Save and plan the next visit'}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
